import React, { useState } from 'react';
import { useLang } from '../i18n/useLang';

const formatConfig = [
  { icon: '🎓', gradientCls: 'group-hover:opacity-100 from-nf-p to-nf-p3' },
  { icon: '🎯', gradientCls: 'group-hover:opacity-100 from-nf-teal to-nf-cyan' },
  { icon: '💬', gradientCls: 'group-hover:opacity-100 from-nf-amber to-[#fde047]' },
];

const statVals = ['120+', '48h', '14', '97%'];

const portalCourseConfig = [
  { icon: '🏥', iconCls: 'bg-[rgba(20,184,166,0.15)]', prog: 68 },
  { icon: '🤖', iconCls: 'bg-[rgba(6,182,212,0.15)]', prog: 0 },
  { icon: '⚡', iconCls: 'bg-[rgba(245,158,11,0.15)]', prog: 0 },
];

export default function Learn({ navigate }) {
  const { t } = useLang();
  const formats = formatConfig.map((cfg, i) => ({ ...cfg, ...t('learn.formats')[i] }));
  const learnStats = statVals.map((val, i) => ({ val, lbl: t('learn.statsLabels')[i] }));
  const tracks = t('learn.tracks');
  const modules = t('learn.modules').map((m, i) => ({ n: `Module ${String(i + 1).padStart(2, '0')}`, ...m }));
  const portalCourses = portalCourseConfig.map((cfg, i) => ({
    ...cfg,
    name: t('learn.portalCourseNames')[i],
    status: t('learn.portalCourseStatuses')[i],
  }));
  const [activeTrack, setActiveTrack] = useState(0);

  return (
    <div>
      {/* Hero */}
      <div className="min-h-[82vh] flex items-center pt-[72px] sm:pt-[80px] pb-8 lg:pb-12 px-4 sm:px-8 lg:px-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 50% 60% at 65% 40%, rgba(109,40,217,.14) 0%, transparent 65%), radial-gradient(ellipse 30% 35% at 15% 75%, rgba(20,184,166,.07) 0%, transparent 60%)' }} />
        <div className="max-w-[1100px] mx-auto w-full flex flex-col lg:flex-row items-center gap-6 lg:gap-0">
          {/* Left */}
          <div className="w-full lg:basis-[60%] relative z-[2]">
            <h1 className="font-heading font-black leading-none tracking-[-0.03em] mb-4 animate-fade-up"
              style={{ fontSize: 'clamp(26px, 3.2vw, 46px)' }}>
              <span className="text-nf-text block">{t('learn.h1')[0]}</span>
              <span className="text-nf-p3 block">{t('learn.h1')[1]}</span>
              <span className="text-nf-teal block">{t('learn.h1')[2]}</span>
              <span className="block text-nf-acc font-bold mt-1.5 tracking-[-0.01em]"
                style={{ fontSize: 'clamp(13px, 1.4vw, 18px)' }}>
                {t('learn.h1Sub')}
              </span>
            </h1>
            <p className="text-[15px] text-nf-t2 leading-[1.75] mb-5">{t('learn.heroBody')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button className="btn-cta btn-lg w-full sm:w-auto">{t('learn.btnExplore')}</button>
              <a href="https://cal.com/willy-carlos-tchuitcheu-lz7ww7/15min?overlayCalendar=true" target="_blank" rel="noopener noreferrer" className="btn-outline w-full sm:w-auto text-center no-underline">{t('learn.btnTalk')}</a>
            </div>
          </div>

          {/* Right — dashboard card */}
          <div className="hidden lg:flex flex-1 justify-end relative z-[2] animate-fade-up-d2">
            <div className="w-[420px] bg-nf-surface border border-[rgba(109,40,217,0.22)] rounded-[18px] overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,.55),0_0_0_0.5px_rgba(109,40,217,.25)]">
              <div className="flex items-center justify-between px-5 py-3.5 bg-nf-bg2 border-b border-white/[0.06]">
                <div className="flex gap-[5px]">
                  <span className="w-[9px] h-[9px] rounded-full bg-[#ff5f57]" />
                  <span className="w-[9px] h-[9px] rounded-full bg-[#febc2e]" />
                  <span className="w-[9px] h-[9px] rounded-full bg-[#28c840]" />
                </div>
                <span className="text-[11px] text-nf-t3">{t('learn.portalLabel')}</span>
                <span className="text-[10px] text-nf-green">{t('learn.portalActive')}</span>
              </div>
              <div className="p-[16px]">
                <div className="font-heading text-[15px] font-bold mb-4">{t('learn.portalJourney')}</div>
                <div className="grid grid-cols-3 gap-2.5 mb-4">
                  {['6', '14h', '3'].map((v, i) => (
                    <div key={i} className="bg-nf-bg2 rounded-[9px] p-3 border border-white/[0.06] text-center">
                      <div className="text-[10px] text-nf-t3 mb-1 uppercase tracking-[0.05em]">{t('learn.portalCourseLabels')[i]}</div>
                      <div className="font-heading text-[18px] font-black text-nf-p3">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-[9px]">
                  {portalCourses.map(m => (
                    <div key={m.name} className="flex items-center gap-3 bg-nf-bg2 rounded-[9px] px-3.5 py-[11px] border border-white/[0.06]">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm shrink-0 ${m.iconCls}`}>{m.icon}</div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold mb-[3px]">{m.name}</div>
                        <div className="text-[10px] text-nf-t3">{m.status}</div>
                        {m.prog > 0 && (
                          <div className="h-[3px] bg-nf-bg3 rounded-sm overflow-hidden mt-[5px]">
                            <div className="h-full rounded-sm bg-gradient-to-r from-nf-p to-nf-p3" style={{ width: `${m.prog}%` }} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formats */}
      <section id="learn-formats" className="py-16 lg:py-24 px-4 sm:px-8 lg:px-20 relative z-[1] bg-nf-bg2">
        <div className="max-w-[1100px] mx-auto">
          <span className="sec-label">{t('learn.formatsLabel')}</span>
          <h2 className="sec-title">{t('learn.formatsTitle')}</h2>
          <p className="sec-sub">{t('learn.formatsSub')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {formats.map((f, i) => (
              <div key={i} className="group bg-nf-bg3 border border-white/[0.06] rounded-2xl px-5 py-6 relative overflow-hidden transition-all duration-300 hover:border-[rgba(109,40,217,0.22)] hover:-translate-y-1">
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r opacity-0 transition-opacity duration-300 ${f.gradientCls}`} />
                <span className="text-[30px] mb-[18px] block">{f.icon}</span>
                <h3 className="font-heading text-[16px] font-extrabold mb-2.5">{f.title}</h3>
                <p className="text-sm text-nf-t2 leading-[1.7] mb-[18px]">{f.desc}</p>
                <div className="flex flex-wrap gap-[7px]">
                  {f.tags.map(t => <span key={t} className="chip">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="bg-nf-bg2 border-t border-b border-white/[0.06]">
        <div className="max-w-[1100px] mx-auto grid grid-cols-2 lg:grid-cols-4">
          {learnStats.map((s, i) => (
            <div key={s.val} className={`py-[28px] px-6 text-center ${i < learnStats.length - 1 ? 'border-r border-white/[0.06]' : ''}`}>
              <div className="font-heading text-[34px] font-black tracking-[-0.04em] mb-1.5 bg-gradient-to-br from-nf-text to-nf-acc bg-clip-text text-transparent">{s.val}</div>
              <div className="text-sm text-nf-t2">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Curriculum */}
      <section id="learn-curriculum" className="py-16 lg:py-24 px-4 sm:px-8 lg:px-20 relative z-[1]">
        <div className="max-w-[1100px] mx-auto">
          <span className="sec-label">{t('learn.curriculumLabel')}</span>
          <h2 className="sec-title">{t('learn.curriculumTitle')}</h2>
          <p className="sec-sub">{t('learn.curriculumSub')}</p>
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 items-start">
            <div className="lg:sticky top-[90px] flex flex-col gap-1">
              {tracks.map((t, i) => (
                <button
                  key={t}
                  className={`flex items-center gap-3 px-4 py-[13px] rounded-[10px] border font-body text-sm cursor-pointer text-left transition-all duration-200 ${activeTrack === i ? 'bg-[rgba(109,40,217,0.12)] border-[rgba(109,40,217,0.22)] text-nf-text' : 'bg-transparent border-transparent text-nf-t2 hover:bg-nf-bg2 hover:text-nf-text'}`}
                  onClick={() => setActiveTrack(i)}>
                  <span className="w-2 h-2 rounded-full bg-nf-p2 shrink-0" />
                  {t}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-3.5">
              {modules.map(m => (
                <div key={m.n} className="flex items-start gap-[18px] bg-nf-surface border border-white/[0.06] rounded-[14px] px-7 py-6 transition-colors hover:border-[rgba(109,40,217,0.22)]">
                  <div className="font-heading text-[13px] font-black text-nf-t3 min-w-[28px] mt-[3px]">{m.n.split(' ')[1]}</div>
                  <div>
                    <h4 className="font-heading text-[17px] font-extrabold mb-1.5">{m.title}</h4>
                    <p className="text-sm text-nf-t2 leading-[1.65] mb-3">{m.body}</p>
                    <div className="flex gap-3 items-center flex-wrap">
                      <span className="text-[11px] px-[11px] py-[3px] rounded-full border border-white/[0.06] text-nf-t3">⏱ {m.dur}</span>
                      <span className="text-[11px] px-[11px] py-[3px] rounded-full border border-white/[0.06] text-nf-t3">{m.format}</span>
                      {m.tag && <span className="text-[11px] px-[11px] py-[3px] rounded-full border border-[rgba(109,40,217,0.3)] text-nf-acc bg-[rgba(109,40,217,0.08)]">{m.tag}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="py-16 lg:py-[120px] px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden bg-nf-bg">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_65%_at_50%_50%,rgba(109,40,217,0.14),transparent_70%)] pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto">
          <h2 className="font-heading font-black leading-[1.05] tracking-[-0.03em] max-w-[740px] mx-auto mb-5"
            style={{ fontSize: 'clamp(30px, 4.5vw, 58px)' }}>
            {t('learn.ctaHeading')[0]}<span className="text-nf-p3">{t('learn.ctaHeading')[1]}</span>
          </h2>
          <p className="text-[17px] text-nf-t2 max-w-[500px] mx-auto mb-11">{t('learn.ctaBody')}</p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
            <button className="btn-cta btn-lg w-full sm:w-auto">{t('learn.ctaBtnPrimary')}</button>
            <button className="btn-outline w-full sm:w-auto" onClick={() => navigate('about')}>{t('learn.ctaBtnSecondary')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
